import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, FileDown, Hand, Mail, MessageCircle, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { fmtMoney } from '../salesI18n';
import { pubUrl } from './publicI18n';
import { PdfViewerModal } from '../../components/PdfViewerModal';

const digits = (s) => (s || '').replace(/\D/g, '');

export const RepCard = ({ d, t, lang, token, onAccept, onInterest, variant = '' }) => {
  const canAct = d.status !== 'accepted' && !d.expired;
  const wa = digits(d.rep.phone);
  const [pdfOpen, setPdfOpen] = useState(false);
  return (
    <div className="rounded-3xl border border-white/80 bg-white/80 backdrop-blur-xl shadow-lg p-5 space-y-4 sales-rise sales-rise-4" data-testid="public-actions">
      {canAct && (
        <div className="space-y-2">
          <Button className="w-full h-12 text-base" onClick={onAccept} data-testid="public-accept-btn"><CheckCircle2 className="h-5 w-5 mr-2" /> {t.accept}</Button>
          <Button variant="outline" className="w-full h-11" onClick={onInterest} data-testid="public-interest-btn"><Hand className="h-4 w-4 mr-2" /> {t.interest}</Button>
        </div>
      )}
      <button type="button" onClick={() => setPdfOpen(true)} className="w-full flex items-center justify-center gap-2 h-11 rounded-full border border-teal-200 bg-white/70 text-teal-800 text-sm font-semibold hover:bg-teal-50 transition-colors" data-testid={`public-pdf-link${variant}`}><FileDown className="h-4 w-4" /> {t.downloadPdf}</button>
      <PdfViewerModal open={pdfOpen} onClose={() => setPdfOpen(false)} src={pubUrl(token, `/pdf?lang=${lang}`)} filename={`${t.proposal} ${d.number}.pdf`} title={`${t.proposal} ${d.number}`} labels={t.pdfViewer} closeLabel={t.cancel} />
      <div className="pt-2 border-t border-slate-200/70">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{t.yourRep}</p>
        <p className="font-bold text-slate-900 mt-0.5" data-testid="public-rep-name">{d.rep.name}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {d.rep.phone && <a href={`tel:${d.rep.phone}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-slate-900 text-white text-xs font-semibold" data-testid="public-rep-call"><Phone className="h-3.5 w-3.5" /> {t.call}</a>}
          {wa && <a href={`https://wa.me/${wa.length <= 10 ? `1${wa}` : wa}?text=${encodeURIComponent(t.whatsMsg(d.number))}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#25D366] text-white text-xs font-semibold" data-testid="public-rep-whatsapp"><MessageCircle className="h-3.5 w-3.5" /> {t.whatsapp}</a>}
          {d.rep.email && <a href={`mailto:${d.rep.email}?subject=${encodeURIComponent(`${t.proposal} ${d.number}`)}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-slate-300 bg-white text-slate-800 text-xs font-semibold" data-testid="public-rep-email"><Mail className="h-3.5 w-3.5" /> {t.email}</a>}
        </div>
      </div>
    </div>
  );
};

export const MobileBar = ({ d, t, onAccept, onInterest }) => {
  if (d.status === 'accepted' || d.expired) return null;
  return (
    <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 p-3 bg-white/85 backdrop-blur-xl border-t border-white/80 shadow-[0_-10px_30px_-20px_rgba(15,23,42,.35)] flex gap-2" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }} data-testid="public-mobile-bar">
      <Button variant="outline" className="flex-1 h-12" onClick={onInterest} data-testid="public-mobile-interest"><Hand className="h-4 w-4 mr-1.5" /> {t.interest}</Button>
      <Button className="flex-[1.4] h-12 text-base" onClick={onAccept} data-testid="public-mobile-accept"><CheckCircle2 className="h-5 w-5 mr-1.5" /> {t.accept}</Button>
    </div>
  );
};

export const AcceptDialog = ({ open, onOpenChange, d, t, lang, token, onDone }) => {
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await fetch(pubUrl(token, '/accept'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
      if (!r.ok) throw new Error((await r.json()).detail || t.error);
      toast.success(t.acceptedToast); onOpenChange(false); onDone(name);
    } catch (e) { toast.error(e.message || t.error); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="public-accept-dialog">
        <DialogHeader><DialogTitle className="font-display">{t.acceptTitle}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{t.acceptHint(d.number, fmtMoney(d.total, lang))}</p>
        <div><Label className="text-xs font-semibold text-slate-600">{t.yourName}</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 mt-1" data-testid="public-accept-name" /></div>
        <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 h-4 w-4 accent-teal-600" data-testid="public-accept-agree" /> {t.agree}</label>
        <div className="flex justify-end gap-2"><Button variant="outline" className="h-11" onClick={() => onOpenChange(false)}>{t.cancel}</Button><Button className="h-11" onClick={submit} disabled={busy || name.trim().length < 2 || !agree} data-testid="public-accept-submit"><CheckCircle2 className="h-4 w-4 mr-1.5" /> {t.confirmAccept}</Button></div>
      </DialogContent>
    </Dialog>
  );
};

export const InterestDialog = ({ open, onOpenChange, t, token, onDone }) => {
  const [f, setF] = useState({ name: '', phone: '', message: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await fetch(pubUrl(token, '/interest'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
      if (!r.ok) throw new Error(t.error);
      toast.success(t.interestSent, { duration: 6000 }); onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e.message || t.error); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="public-interest-dialog">
        <DialogHeader><DialogTitle className="font-display">{t.interestTitle}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{t.interestHint}</p>
        <div><Label className="text-xs font-semibold text-slate-600">{t.yourName}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11 mt-1" data-testid="public-interest-name" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">{t.phone}</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="h-11 mt-1" data-testid="public-interest-phone" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">{t.message}</Label><Textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={3} className="mt-1" data-testid="public-interest-message" /></div>
        <div className="flex justify-end gap-2"><Button variant="outline" className="h-11" onClick={() => onOpenChange(false)}>{t.cancel}</Button><Button className="h-11" onClick={submit} disabled={busy} data-testid="public-interest-submit"><Hand className="h-4 w-4 mr-1.5" /> {t.send}</Button></div>
      </DialogContent>
    </Dialog>
  );
};
